import React, { useState, useEffect } from "react";
import Moment from 'moment';
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { visuallyHidden } from '@mui/utils';
import { Tooltip } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Grid from "@mui/material/Unstable_Grid2";
import Box from "@mui/material/Box";
import { Container } from '@mui/system';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';import Typography from "@mui/material/Typography";
import "./App.css";
import { useInitialData, useOffenseData } from "./hooks";
import { SentenceTimeline, Warning, InfoTabs } from "./components";
import { formatNumber } from "./utils";
import { Offense, PriorCommitments } from "./types";
//import { track } from "./analytics";
import { Onboarding } from "./components/Onboarding";
import { MenuItem, Select, SelectChangeEvent } from "@mui/material";
import LastRevision from "../package.json";

const INITIAL_OFFENSE_CODE = "579.015-001Y202235";
const INITIAL_OFFENSE_DESCRIPTION =
  "POSSESSION OF CONTROLLED SUBSTANCE";

function App() {
  const theme = useTheme();
  
  const tabletWidth = useMediaQuery(theme.breakpoints.down("lg"));
  const { offenses } = useInitialData();
  const {
    data: offenseData,
    updateData,
    updateSentence,
    updatePriorCommitments,
    isCurrentFelonyDangerous,
  } = useOffenseData();

  const [open, setOpen] = React.useState(false);
  const [startOnboarding, setStartOnboarding] = useState(false);


  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };
  // State that depends on user input
  const [offense, setOffense] = useState<Offense>({
    code: INITIAL_OFFENSE_CODE,
    description: `${INITIAL_OFFENSE_CODE}: ${INITIAL_OFFENSE_DESCRIPTION}`,
  });
  const [sentenceImposed, setSentenceImposed] = useState<number>(1); // in years
  const [jailTime, setJailTime] = useState<number>(0); // in months
  const [priorCommitments, setPriorCommitments] =
    useState<PriorCommitments>("0"); // in months

  const filterOptions = createFilterOptions({
    matchFrom: "any",
    stringify: (option: Offense) => option.description,
  });
  
  //State to determine whether the unClassifiedDialogue is open
  const [unclassifiedDialogOpen, setUnclassifiedDialogOpen] = useState(false);
  const [statuteLink, setStatuteLink] = useState<string>("https://revisor.mo.gov/main/Home.aspx");
  const [baseCode, setBaseCode] = useState<string>("");

  // window.Intercom("boot", {
  //   api_base: "https://api-iam.intercom.io",
  //   app_id: "b0upuo23",
  // });

  // Runs when app loads
  useEffect(() => {
    const returnData = updateData(INITIAL_OFFENSE_CODE);
    if (returnData) {
      setSentenceImposed(returnData.defaultSentence);
    }
  }, [updateData]);

  if (!offenseData) return null;

  
  Moment.locale('en');
  var dt = LastRevision.dateLastRevised;

  return (
    <><Box
      sx={{
        //p: { lg: "64px 128px 0 48px", md: "48px", sm: "24px", xs: "12px" },
        p: { lg: "25px 128px 0 48px", md: "25px", sm: "20px", xs: "12px" },
      }}
    >
        <Onboarding
          startOnboarding={startOnboarding}
          setStartOnboarding={setStartOnboarding} />
        <Grid container display="flex" flexDirection="column">
          {tabletWidth && (
            <Grid>
              <Typography
                      id="app-title"
                      variant="h4"
                      component="h1"
                      fontWeight="600"
                      display="flex" flexDirection="row"
                      sx={{ mb: "16px", width: "100%", justifyContent: 'center', alignItems: 'center' }}
                    >
                      <img id="sentencing-calc-logo" src={'./sentencingcalclogo.svg'} alt="" />
                      <Typography component="span" sx={visuallyHidden}>Missouri Time Served Calculator{" "}</Typography>
                      <Tooltip title="Learn about the features of the Missouri Time Served Calculator" arrow>
                        <IconButton aria-label="Start User Guide" onClick={() => setStartOnboarding(true)} sx={{ cursor: "pointer" }}>
                          <span style={visuallyHidden}>Start User Guide</span>
                          <HelpOutlineIcon color="primary" />
                        </IconButton>
                      </Tooltip>
                    </Typography>
              <Box sx={{ mb: "32px" }}>
                <Typography variant="body1" gutterBottom>
                  Use this tool to understand the approximate range of time a felony offender may serve in prison based on the sentence imposed for a specific charge, as well as the potential window of time between parole eligibility and conditional release.
                </Typography>
                <Typography variant="body1" gutterBottom>
                  Estimates are based on current law (statutes and regulations) as of <Box component="span" fontWeight='fontWeightBold'>{Moment(dt).format('MM/DD/YYYY')}</Box>.
                </Typography>
                <Typography variant="body1">
                  The information provided is for general informational purposes only and is not intended to guide or guarantee any particular outcome.
                </Typography>
              </Box>
            </Grid>
          )}
          <Grid container display="flex" flexDirection="row">
            <Grid 
            xl={3} 
            lg={4} 
            md={6} 
            sm={6} 
            xs={12}
            sx={{
              mb: { xs: "20px" }
            }}
            >
              <Box
              sx={{
                '--Grid-borderWidth': '1px',
                borderTop: 'var(--Grid-borderWidth) solid',
                borderBottom: 'var(--Grid-borderWidth) solid',
                borderLeft: 'var(--Grid-borderWidth) solid',
                borderRight: 'var(--Grid-borderWidth) solid',
                borderColor: 'divider',
                p: '10px',
                borderRadius: '10px',
              }}
              >
                <div id="charge-code" className="sentence-name">
                  <label htmlFor="combo-box-demo" className="menu-descriptions"> Charge </label><br />
                  <Typography variant="body2" sx={{ mb: "4px"}}>In the field below, type in a felony charge or select from the list of felony charge codes. Once you select a charge code, you can view information about the felony class, offense type and relevant sentencing laws. These charge codes are drawn from the Missouri Charge Code Manual</Typography>
                  <Autocomplete
                    id="combo-box-demo"
                    options={offenses}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        InputProps={{
                          ...params.InputProps,
                          style: { borderRadius: "8px", padding: "12px 16px" },
                        }}
                        inputProps={{
                          ...params.inputProps,
                          style: {
                            fontWeight: "bold",
                            fontSize: tabletWidth ? "18px" : "24px",
                            padding: 0,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          },
                        }} />
                    )}
                    value={offense}
                    onChange={(_, value) => {
                      if (value !== null) {
                        setOffense(value);
                        const returnData = updateData(value.code);
                        if (returnData) {
                          setPriorCommitments("0");
                          setSentenceImposed(returnData.defaultSentence);
                    
                          // Check if the felony class is unclassified
                          if (returnData.felonyClass === "U") {
                            const extractedBaseCode = value.code.split("-")[0]; // Get part before the dash
                            setBaseCode(extractedBaseCode); // Save for access elsewhere
                            setStatuteLink(`https://revisor.mo.gov/main/OneSection.aspx?section=${extractedBaseCode}`);
                            setUnclassifiedDialogOpen(true); // Open the dialog
                          }
                        }
                      }
                    }}
                    
                    getOptionLabel={(option) => option.code}
                    renderOption={(props, option) => (
                      <li {...props}>{option.description}</li>
                    )}
                    isOptionEqualToValue={(option, value) => option.description === value.description}
                    filterOptions={filterOptions}
                    disablePortal
                    fullWidth={true} />
                </div>
                <Dialog 
                  open={unclassifiedDialogOpen} 
                  onClose={() => {
                    setUnclassifiedDialogOpen(false);
                    setOffense({
                      code: INITIAL_OFFENSE_CODE,
                      description: `${INITIAL_OFFENSE_CODE}: ${INITIAL_OFFENSE_DESCRIPTION}`
                    });

                    // Ensure the app registers the change
                    const returnData = updateData(INITIAL_OFFENSE_CODE);
                    if (returnData) {
                      setSentenceImposed(returnData.defaultSentence);
                      setPriorCommitments("0");
                    }
                  }}
                >
                  <DialogTitle>Unclassified Felony</DialogTitle>
                  <DialogContent>
                    <DialogContentText>
                      Unclassified felonies are offenses for which Missouri law does not assign a specific felony class but instead provides a unique sentencing range. The punishment for these offenses is typically outlined in the specific statute defining the crime, rather than following the general sentencing structure for Class A–E felonies. Please check <a target="_blank" rel="noopener noreferrer" href={statuteLink}>§ {baseCode}, RSMo, on the Missouri Revisor of Statutes website</a> for statutorily outlined sentencing requirements.
                    </DialogContentText>
                  </DialogContent>
                  <DialogActions>
                    <Button 
                      onClick={() => {
                        setUnclassifiedDialogOpen(false);
                        setOffense({
                          code: INITIAL_OFFENSE_CODE,
                          description: `${INITIAL_OFFENSE_CODE}: ${INITIAL_OFFENSE_DESCRIPTION}`
                        });

                        // Ensure the app registers the change
                        const returnData = updateData(INITIAL_OFFENSE_CODE);
                        if (returnData) {
                          setSentenceImposed(returnData.defaultSentence);
                          setPriorCommitments("0");
                        }
                      }} 
                      color="primary"
                    >
                      OK
                    </Button>
                  </DialogActions>
                </Dialog>


                <div className="incident-name">{offenseData["Incident Name"]}</div>
                <div className="felony-class-offense-type">
                  <div className="felony-class-offense-type-row">
                    <span> Felony Class</span>{" "}
                    {offenseData["Felony Class Expanded"]}
                  </div>
                  <div className="felony-class-offense-type-row">
                    <span>Offense Type </span>
                    {offenseData["Parole Offense Type Expanded"]}
                  </div>
                </div>
              </Box>

              <div id="inputs">
                <Box
                sx={{
                  '--Grid-borderWidth': '1px',
                  borderTop: 'var(--Grid-borderWidth) solid',
                  borderBottom: 'var(--Grid-borderWidth) solid',
                  borderLeft: 'var(--Grid-borderWidth) solid',
                  borderRight: 'var(--Grid-borderWidth) solid',
                  borderColor: 'divider',
                  p: '10px',
                  mt: '30px',
                  borderRadius: '10px',
                }}
                >
                  <label htmlFor="outlined-number-0" className="menu-descriptions"> Sentence Imposed (years) </label><br />
                  <Typography variant="body2" sx={{ mb: "4px"}}>From the range provided, please enter the number of years imposed or contemplated to be imposed in the sentence field.</Typography>
                  <div>
                    <TextField
                      id="outlined-number-0"
                      type="number"
                      value={sentenceImposed}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const newSentence = parseInt(event.target.value);
                        if (newSentence) {
                          setSentenceImposed(newSentence);
                          const { maxPercentage } = updateSentence(
                            newSentence,
                            priorCommitments
                          );
                          // track("update_sentence", {
                          //   charge_code: offense.code,
                          //   sentence_imposed: newSentence,
                          //   jail_time: jailTime,
                          //   minimum_percentage: offenseData["Minimum Percentage"],
                          //   maximum_percentage: maxPercentage,
                          // });
                        }
                      } }
                      InputProps={{
                        style: { borderRadius: "8px", padding: "12px 16px" },
                      }}
                      inputProps={{
                        min: 1,
                        max: formatNumber(offenseData["Sentencing Range"][1]),
                        style: {
                          fontWeight: "bold",
                          fontSize: tabletWidth ? "18px" : "24px",
                          padding: 0,
                        },
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth={true} />
                  </div>

                  <div className="sentencing-range">
                    <span>Sentencing Range </span>
                    {formatNumber(offenseData["Sentencing Range"][0])} -{" "}
                    {formatNumber(offenseData["Sentencing Range"][1])} years
                  </div>
                <div>
                  <Warning
                    enteredSentenceImposed={sentenceImposed}
                    maxSentenceImposed={Number(
                      offenseData["Sentencing Range"][1]
                      )} />
                </div>
                </Box>

                <Box
                className="input-wrapper"
                sx={{
                  '--Grid-borderWidth': '1px',
                  borderTop: 'var(--Grid-borderWidth) solid',
                  borderBottom: 'var(--Grid-borderWidth) solid',
                  borderLeft: 'var(--Grid-borderWidth) solid',
                  borderRight: 'var(--Grid-borderWidth) solid',
                  borderColor: 'divider',
                  p: '10px',
                  borderRadius: '10px',
                }}
                >
                  <div className="jail-time-container">
                    <label htmlFor="outlined-number-1" className="menu-descriptions">
                      Jail Time Credited (months){" "}
                    </label><br />
                    <Typography variant="body2">Please type into the field the number of months – if any – the offender already has served in jail for this offense.</Typography>
                    <div className="jail-time-input">
                      <TextField
                        sx={{ mb: "16px" }}
                        id="outlined-number-1"
                        value={jailTime}
                        onChange={(
                          event: React.ChangeEvent<HTMLInputElement>
                        ) => {
                          const newJailTime = parseInt(event.target.value);
                          if (!isNaN(newJailTime)) {
                            setJailTime(newJailTime);
                            // track("update_jail_time", {
                            //   charge_code: offense.code,
                            //   sentence_imposed: sentenceImposed,
                            //   jail_time: newJailTime,
                            //   minimum_percentage:
                            //     offenseData["Minimum Percentage"],
                            //   maximum_percentage:
                            //     offenseData["Maximum Percentage"],
                            // });
                          }
                        } }
                        type="number"
                        InputProps={{
                          style: { borderRadius: "8px", padding: "12px 16px" },
                        }}
                        inputProps={{
                          min: 0,
                          style: {
                            fontWeight: "bold",
                            fontSize: tabletWidth ? "18px" : "24px",
                            padding: 0,
                          },
                        }}
                        InputLabelProps={{
                          shrink: true,
                        }}
                        fullWidth={true} />
                      {offenseData["Minimum Percentage"] !== undefined &&
                        jailTime >
                        sentenceImposed *
                        offenseData["Minimum Percentage"] *
                        12 ? (
                        <p
                          style={{
                            color: "black",
                            textAlign: "left",
                          }}
                        >
                          Individual will be eligible for parole upon intake to
                          Department of Corrections.
                        </p>
                      ) : null}
                    </div>
                  </div>
                </Box>

                {offenseData.isMPT && !isCurrentFelonyDangerous &&(
                  <Box
                  sx={{
                    '--Grid-borderWidth': '1px',
                    borderTop: 'var(--Grid-borderWidth) solid',
                    borderBottom: 'var(--Grid-borderWidth) solid',
                    borderLeft: 'var(--Grid-borderWidth) solid',
                    borderRight: 'var(--Grid-borderWidth) solid',
                    borderColor: 'divider',
                    p: '10px',
                    mt: '30px',
                    borderRadius: '10px',
                  }}
                  >
                  <div className="input-wrapper">
                    <div>
                      <p className="menu-descriptions">
                        Number of prior felony commitments for offenses listed in{" "}
                        <a
                          href="https://revisor.mo.gov/main/OneSection.aspx?section=558.019&bid=47815&hl"
                          target="_blank"
                          rel="noreferrer"
                        >
                          § 558.019, RSMo
                        </a>
                      </p>
                      <div>
                        <Select
                          sx={{ mb: "16px", borderRadius: "8px" }}
                          value={priorCommitments}
                          onChange={(event: SelectChangeEvent) => {
                            setPriorCommitments(
                              event.target.value as PriorCommitments
                            );
                            updatePriorCommitments(
                              sentenceImposed,
                              event.target.value as PriorCommitments,
                              offenseData["Parole Offense Type"],
                              offenseData["Felony Class"],
                              offenseData?.isMPT
                            );
                          } }
                          fullWidth
                          SelectDisplayProps={{
                            style: {
                              borderRadius: "8px",
                              padding: "12px 16px",
                              fontWeight: "bold",
                              fontSize: tabletWidth ? "18px" : "24px",
                              display: "flex",
                              alignItems: "center",
                            },
                          }}
                        >
                          <MenuItem value="0">0</MenuItem>
                          <MenuItem value="1">1</MenuItem>
                          <MenuItem value="2">2</MenuItem>
                          <MenuItem value="3+">3+</MenuItem>
                        </Select>
                      </div>
                    </div>
                  </div>
                  </Box>
                )}
              </div>
              <div className="m-link">
                <Typography>
                  <Link
                    component="button"
                    variant="body1"
                    onClick={handleClickOpen}
                  >
                    Disclaimer
                  </Link>
                </Typography>
                {/* <Typography>
                  <Link variant="body1" href="/file.jsp?id=2021">
                    Methodology
                  </Link>
                </Typography> */}
              </div>
              <React.Fragment>
      <Dialog
        open={open}
        onClose={handleClose}
      >
        <DialogTitle>Disclaimer</DialogTitle>
        <DialogContent className="disclaimer-text">
          <Typography sx={{ mb: "16px"}}>
            The Missouri Time Served Calculator is designed for general informational purposes only. All information is based on relevant statutes and regulations. The information the tool provides does not constitute legal advice and is not intended to guide or guarantee any particular outcome.
          </Typography>
          <Typography sx={{ mb: "16px"}}>
            The Missouri Sentencing Advisory Commission and its partners make no representation or warranty – express or implied – about the accuracy, completeness, timeliness, or applicability of the information the tool provides and are not liable for any errors or omissions.
          </Typography>
          <Typography sx={{ mb: "16px"}}>
            Users of this tool assume full responsibility for the use of the information provided and agree the Missouri Sentencing Advisory Commission nor any of its partners are not responsible or liable for any actions taken in reliance on the information provided or any claim, loss, or damage arising from any use of the tool.
          </Typography>
          <Typography sx={{ mb: "16px"}}>
            This tool reflects data last updated <Box component="span" fontWeight='fontWeightBold'>{Moment(dt).format('M/DD/YYYY')}</Box>.
          </Typography>
            {/* <Typography>
            To learn more about how the tool works, please read the please read the <a href="#">in-depth methodology explanation</a>.
            </Typography> */}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
            </Grid>
            <Grid
              xl={8}
              xlOffset={1}
              lg={7}
              lgOffset={1}
              md={5}
              mdOffset={1}
              sm={5}
              smOffset={1}
              xs={12}
              container
              display="flex"
              flexDirection="column"
            >
              {!tabletWidth && (
                <Grid display="flex"alignItems="center" >

                  <Box>
                    <Typography
                      id="app-title"
                      variant="h4"
                      component="h1"
                      fontWeight="600"
                      display="flex" flexDirection="row"
                      sx={{ mb: "16px", width: "100%", justifyContent: 'center', alignItems: 'center' }}
                    >
                      <img id="sentencing-calc-logo" src={'./sentencingcalclogo.svg'} alt="" />
                      <Typography component="span" sx={visuallyHidden}>Missouri Time Served Calculator{" "}</Typography>
                      <Tooltip title="Learn about the features of the Missouri Time Served Calculator" arrow>
                        <IconButton aria-label="Start User Guide" onClick={() => setStartOnboarding(true)} sx={{ cursor: "pointer" }}>
                          <span style={visuallyHidden}>Start User Guide</span>
                          <HelpOutlineIcon color="primary" />
                        </IconButton>
                      </Tooltip>
                    </Typography>
                    <Box sx={{ mb: "24px" }}>
                      <Typography variant="body1" gutterBottom>
                        Use this tool to understand the approximate range of time a felony offender may serve in prison based on the sentence imposed for a specific charge, as well as the potential window of time between parole eligibility and conditional release.
                      </Typography>
                      <Typography variant="body1" gutterBottom>
                        Estimates are based on current law (statutes and regulations) as of <Box component="span" fontWeight='fontWeightBold'>{Moment(dt).format('M/DD/YYYY')}</Box>.
                      </Typography>
                      <Typography variant="body1">
                        The information provided is for general informational purposes only and is not intended to guide or guarantee any particular outcome.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}

              <Grid container display="flex" flexDirection="row">
                <Grid id="timeline" lg={6} sm={12}>
                <Typography
                    variant="h6"
                    component="h2"
                  > Range of Time to be Served </Typography>
                  <SentenceTimeline
                    totalSentence={sentenceImposed * 12}
                    minPercent={Number(
                      (offenseData["Minimum Percentage"] * 100).toFixed(2)
                    )}
                    maxPercent={Number(
                      (offenseData["Maximum Percentage"] * 100).toFixed(2)
                    )}
                    jailTimeMonths={jailTime} />
                </Grid>

                {!tabletWidth && (
                  <Grid id="rules" lg={5} lgOffset={1}>
                    <Typography 
                        variant="h6"
                        component="h2"
                        sx={{ mb: "15px" }}
                    >Relevant Law</Typography>
                    <InfoTabs offenseData={offenseData} isCurrentFelonyDangerous={isCurrentFelonyDangerous}/>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
          {tabletWidth && (
            <Grid id="rules">
              <h5>Relevant Law</h5>
              <InfoTabs offenseData={offenseData} isCurrentFelonyDangerous={isCurrentFelonyDangerous}/>
            </Grid>
          )}
        </Grid>
      </Box></>
  );
}

export default App;
